/**
 * ConsignmentOrdersAdminTab
 *
 * Mirrors B2BOrdersAdminTab for consignment orders (order_type = 'consignment').
 * Plugged into ConsignmentPage as the first tab.
 *
 * Status transitions use b2b_lock_consignment_order (instead of b2b_lock_order)
 * which chains the approved order to a "תעודת משלוח קונסגנציה" document.
 */

import {
  useEffect, useState, useCallback, useMemo, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { Loader as Loader2, CircleAlert as AlertCircle, ShoppingBag, X, CircleCheck as CheckCircle2, Circle as XCircle, Clock, Truck, Lock, ChevronRight, RefreshCw, User, Mail, Phone, Hash, Boxes, Warehouse } from 'lucide-react';
import { supabase }          from '@/lib/supabase';
import { usePermissions }    from '@/hooks/usePermissions';
import { OrderPrintActions, SelectionBar, RowCheckbox } from '@/components/inventory/IncomingDocsPrint';
import type { PrintableOrder } from '@/lib/incomingDocsExport';

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderStatus = 'pending' | 'processing' | 'locked' | 'cancelled';

interface AdminOrderLine {
  id:                 string;
  product_id:         string;
  product_name:       string;
  product_sku:        string;
  product_image_url:  string | null;
  requested_quantity: number;
  approved_quantity:  number;
  unit_price:         number;
  total_line_price:   number;
}

interface AdminOrder {
  id:           string;
  short_id:     string;
  status:       OrderStatus;
  total_amount: number;
  created_at:   string;
  updated_at:   string;
  client_id:    string;
  client_name:  string;
  client_email: string | null;
  client_phone: string | null;
  lines:        AdminOrderLine[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtPrice = (n: number) =>
  `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const shortId = (id: string) => id.slice(-8).toUpperCase();

const PRINT_TITLE = 'הזמנת קונסגנציה נכנסת';
const FILE_PREFIX = 'הזמנת קונסגנציה';

function toPrintable(order: AdminOrder, vatRate: number): PrintableOrder {
  return {
    id:          order.id,
    shortId:     order.short_id,
    statusLabel: STATUS_META[order.status]?.label ?? order.status,
    isOpen:      order.status === 'pending' || order.status === 'processing',
    createdAt:   order.created_at,
    clientName:  order.client_name,
    clientEmail: order.client_email,
    clientPhone: order.client_phone,
    vatRate,
    lines: order.lines.map(l => ({
      name:      l.product_name,
      sku:       l.product_sku,
      imageUrl:  l.product_image_url,
      requested: l.requested_quantity,
      approved:  l.approved_quantity,
      unitPrice: l.unit_price,
      lineTotal: l.total_line_price,
    })),
  };
}

// ─── Status meta ──────────────────────────────────────────────────────────────

const STATUS_META: Record<OrderStatus, {
  label: string;
  icon:  React.ReactNode;
  pill:  string;
  row:   string;
}> = {
  pending: {
    label: 'ממתינה לאישור',
    icon:  <Clock      className="w-3.5 h-3.5" />,
    pill:  'bg-amber-100  text-amber-700  border-amber-200',
    row:   '',
  },
  processing: {
    label: 'בטיפול',
    icon:  <Truck      className="w-3.5 h-3.5" />,
    pill:  'bg-blue-100   text-blue-700   border-blue-200',
    row:   'bg-blue-50/30',
  },
  locked: {
    label: 'אושרה סופית',
    icon:  <Lock       className="w-3.5 h-3.5" />,
    pill:  'bg-teal-100   text-teal-700   border-teal-200',
    row:   'bg-teal-50/30',
  },
  cancelled: {
    label: 'בוטלה',
    icon:  <XCircle    className="w-3.5 h-3.5" />,
    pill:  'bg-gray-100   text-gray-500   border-gray-200',
    row:   'opacity-60',
  },
};

function StatusBadge({ status }: { status: OrderStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                      text-[11px] font-semibold border leading-none ${m.pill}`}>
      {m.icon}
      {m.label}
    </span>
  );
}

// ─── Qty cell ─────────────────────────────────────────────────────────────────

function QtyCell({
  line,
  editable,
  onChange,
  saving,
}: {
  line:     AdminOrderLine;
  editable: boolean;
  onChange: (lineId: string, newQty: number) => void;
  saving:   boolean;
}) {
  const modified = line.approved_quantity !== line.requested_quantity;
  const reduced  = line.approved_quantity < line.requested_quantity;

  return (
    <div className="flex flex-col items-end gap-1 min-w-[110px]">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-gray-400 font-medium">מאושר:</span>
        {editable ? (
          <input
            type="number"
            min={0}
            max={line.requested_quantity * 10}
            value={line.approved_quantity}
            disabled={saving}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v) && v >= 0) onChange(line.id, v);
            }}
            className={`w-16 text-center text-sm font-bold rounded-lg border
                        px-1.5 py-1 focus:outline-none focus:ring-2
                        disabled:opacity-50 transition-colors
                        ${modified
                          ? 'border-amber-300 bg-amber-50 text-amber-700 focus:ring-amber-300'
                          : 'border-gray-200 bg-white text-gray-900 focus:ring-teal-300'
                        }`}
          />
        ) : (
          <span className={`text-sm font-bold tabular-nums
                            ${modified ? (reduced ? 'text-amber-600' : 'text-teal-600') : 'text-gray-800'}`}>
            {line.approved_quantity.toLocaleString('he-IL')}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-gray-400 font-medium">הוזמן:</span>
        <span className={`text-xs tabular-nums
                          ${modified ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
          {line.requested_quantity.toLocaleString('he-IL')}
        </span>
      </div>
      {modified && (
        <span className={`text-[9px] font-bold uppercase tracking-wide
                          px-1.5 py-0.5 rounded-full border leading-none
                          ${reduced
                            ? 'bg-amber-50 text-amber-600 border-amber-200'
                            : 'bg-teal-50 text-teal-600 border-teal-200'
                          }`}>
          {reduced ? '▼ הופחת' : '▲ הוגדל'}
        </span>
      )}
    </div>
  );
}

// ─── Confirmation button ──────────────────────────────────────────────────────

function ConfirmButton({
  label, labelConfirm, onClick, disabled, className, icon,
}: {
  label:        string;
  labelConfirm: string;
  onClick:      () => void;
  disabled:     boolean;
  className:    string;
  icon:         React.ReactNode;
}) {
  const [armed, setArmed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = () => {
    if (!armed) {
      setArmed(true);
      timer.current = setTimeout(() => setArmed(false), 3000);
    } else {
      clearTimeout(timer.current!);
      setArmed(false);
      onClick();
    }
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                  text-sm font-semibold transition-all active:scale-[.98]
                  disabled:opacity-40 disabled:cursor-not-allowed ${className}
                  ${armed ? 'ring-2 ring-offset-1 ring-current' : ''}`}
    >
      {icon}
      {armed ? labelConfirm : label}
    </button>
  );
}

// ─── Warehouse picker modal ───────────────────────────────────────────────────

interface Warehouse { id: string; name: string; }

function WarehousePickerModal({
  companyId,
  destinationWarehouseName,
  onConfirm,
  onCancel,
}: {
  companyId:                string;
  destinationWarehouseName: string;
  onConfirm:                (sourceWarehouseId: string) => void;
  onCancel:                 () => void;
}) {
  const [warehouses,  setWarehouses]  = useState<Warehouse[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState<string>('');

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('inventory_warehouses')
        .select('id, name')
        .eq('company_id', companyId)
        .order('name');
      setWarehouses(data ?? []);
      setLoading(false);
    })();
  }, [companyId]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[300] bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div dir="rtl" className="fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                                 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-teal-50/40">
          <div className="flex items-center gap-2">
            <Warehouse className="w-4 h-4 text-teal-600" />
            <h3 className="text-base font-bold text-gray-900">בחר מחסן מוצא</h3>
          </div>
          <button onClick={onCancel} className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-start gap-2 px-3 py-2.5 bg-teal-50 border border-teal-200 rounded-xl text-xs text-teal-800">
            <Warehouse className="w-3.5 h-3.5 shrink-0 mt-0.5 text-teal-500" />
            <span>
              מחסן יעד (מחסן הלקוח): <strong>{destinationWarehouseName}</strong>
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">מחסן מוצא</label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> טוען מחסנים…
              </div>
            ) : (
              <select
                value={selected}
                onChange={e => setSelected(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm
                           focus:outline-none focus:ring-2 focus:ring-teal-300 bg-white"
              >
                <option value="">— בחר מחסן —</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl
                       hover:bg-gray-50 transition-colors">
            ביטול
          </button>
          <button
            onClick={() => selected && onConfirm(selected)}
            disabled={!selected}
            className="px-4 py-2 text-sm font-semibold text-white bg-teal-600
                       rounded-xl hover:bg-teal-700 transition-colors
                       disabled:opacity-40 disabled:cursor-not-allowed">
            אשר והפק תעודת העברה
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

// ─── Fulfillment Drawer ───────────────────────────────────────────────────────

function FulfillmentDrawer({
  order, canEdit, onClose, onOrderUpdated, companyId, vatRate,
}: {
  order:          AdminOrder;
  canEdit:        boolean;
  onClose:        () => void;
  onOrderUpdated: (updated: AdminOrder) => void;
  companyId:      string;
  vatRate:        number;
}) {
  const [lines,  setLines]  = useState<AdminOrderLine[]>(order.lines);
  const [status, setStatus] = useState<OrderStatus>(order.status);
  const [saving, setSaving] = useState(false);
  const [toast,  setToast]  = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [showWarehousePicker, setShowWarehousePicker] = useState(false);
  const [destinationWarehouseName, setDestinationWarehouseName] = useState<string>('');

  const editable = canEdit && (status === 'pending' || status === 'processing');

  const showToast = (kind: 'success' | 'error', msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // Load the client's consignment warehouse name for display in the picker
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('inventory_clients')
        .select('consignment_warehouse_id, inventory_warehouses!consignment_warehouse_id(name)')
        .eq('id', order.client_id)
        .maybeSingle();
      if (data) {
        const wh = data.inventory_warehouses as unknown as { name: string } | null;
        setDestinationWarehouseName(wh?.name ?? '');
      }
    })();
  }, [order.client_id]);

  const pendingUpdates = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const handleQtyChange = useCallback((lineId: string, newQty: number) => {
    setLines(prev =>
      prev.map(l => l.id === lineId
        ? { ...l, approved_quantity: newQty, total_line_price: newQty * l.unit_price }
        : l,
      ),
    );
    if (pendingUpdates.current.has(lineId)) clearTimeout(pendingUpdates.current.get(lineId)!);
    pendingUpdates.current.set(lineId, setTimeout(async () => {
      pendingUpdates.current.delete(lineId);
      const { error } = await supabase
        .from('b2b_order_lines')
        .update({ approved_quantity: newQty })
        .eq('id', lineId);
      if (error) {
        showToast('error', `שגיאה בשמירת הכמות: ${error.message}`);
        setLines(prev =>
          prev.map(l => l.id === lineId
            ? { ...l, approved_quantity: order.lines.find(ol => ol.id === lineId)?.approved_quantity ?? l.approved_quantity }
            : l,
          ),
        );
      }
    }, 600));
  }, [order.lines]);

  const flushPendingQtyUpdates = async () => {
    const promises: Promise<void>[] = [];
    for (const [lineId, timer] of pendingUpdates.current.entries()) {
      clearTimeout(timer);
      pendingUpdates.current.delete(lineId);
      const line = lines.find(l => l.id === lineId);
      if (line) {
        promises.push(
          Promise.resolve(
            supabase.from('b2b_order_lines').update({ approved_quantity: line.approved_quantity }).eq('id', lineId),
          ).then(({ error }) => { if (error) throw new Error(error.message); }),
        );
      }
    }
    await Promise.all(promises);
  };

  const transition = useCallback(async (
    rpcName: 'b2b_set_order_processing' | 'b2b_cancel_order',
    nextStatus: OrderStatus,
    successMsg: string,
  ) => {
    setSaving(true);
    try {
      await flushPendingQtyUpdates();
      const { error } = await supabase.rpc(rpcName, { p_order_id: order.id });
      if (error) {
        const hint = error.hint ? ` (${error.hint})` : '';
        const detail = error.details ? ` — ${error.details}` : '';
        throw new Error(`${error.message}${detail}${hint}`);
      }
      const { data: fresh } = await supabase
        .from('b2b_orders').select('total_amount, updated_at').eq('id', order.id).single();
      setStatus(nextStatus);
      showToast('success', successMsg);
      onOrderUpdated({
        ...order, lines, status: nextStatus,
        total_amount: Number(fresh?.total_amount ?? order.total_amount),
        updated_at:   fresh?.updated_at ?? order.updated_at,
      });
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, lines, onOrderUpdated]);

  const lockWithWarehouse = useCallback(async (sourceWarehouseId: string) => {
    setShowWarehousePicker(false);
    setSaving(true);
    try {
      await flushPendingQtyUpdates();
      const { error } = await supabase.rpc('b2b_lock_consignment_order', {
        p_order_id:            order.id,
        p_source_warehouse_id: sourceWarehouseId,
      });
      if (error) {
        const hint = error.hint ? ` (${error.hint})` : '';
        const detail = error.details ? ` — ${error.details}` : '';
        throw new Error(`${error.message}${detail}${hint}`);
      }
      const { data: fresh } = await supabase
        .from('b2b_orders').select('total_amount, updated_at').eq('id', order.id).single();
      setStatus('locked');
      showToast('success', 'ההזמנה נחתמה — תעודת העברה בין מחסנים קונסגנציה הופקה');
      onOrderUpdated({
        ...order, lines, status: 'locked',
        total_amount: Number(fresh?.total_amount ?? order.total_amount),
        updated_at:   fresh?.updated_at ?? order.updated_at,
      });
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, lines, onOrderUpdated]);

  const localTotal = useMemo(
    () => lines.reduce((s, l) => s + l.total_line_price, 0),
    [lines],
  );

  const hasModified = lines.some(l => l.approved_quantity !== l.requested_quantity);

  const printable = useMemo(
    () => [toPrintable({ ...order, lines, status }, vatRate)],
    [order, lines, status, vatRate],
  );

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm"
        onClick={() => !saving && onClose()}
      />
      <div dir="rtl" className="fixed top-0 right-0 bottom-0 z-[201] w-full max-w-xl
                                 bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4
                        border-b border-gray-100 bg-teal-50/40 shrink-0">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Boxes className="w-4 h-4 text-teal-600" />
              <span className="text-xs font-bold text-teal-700 uppercase tracking-wide">קונסגנציה</span>
            </div>
            <StatusBadge status={status} />
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400 shrink-0" />
              {order.client_name}
            </h2>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
              {order.client_email && (
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{order.client_email}</span>
              )}
              {order.client_phone && (
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{order.client_phone}</span>
              )}
              <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{order.short_id}</span>
              <span>{fmtDateTime(order.created_at)}</span>
            </div>
          </div>
          <button onClick={onClose} disabled={saving}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200
                       rounded-lg transition-colors disabled:opacity-40 shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-gray-100 bg-white shrink-0">
          <OrderPrintActions orders={printable} title={PRINT_TITLE} filePrefix={FILE_PREFIX} />
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mx-4 mt-3 flex items-center gap-2.5 px-4 py-2.5
                           rounded-xl text-sm font-medium shrink-0
                           ${toast.kind === 'success'
                             ? 'bg-teal-50 border border-teal-200 text-teal-700'
                             : 'bg-red-50 border border-red-200 text-red-700'
                           }`}>
            {toast.kind === 'success'
              ? <CheckCircle2 className="w-4 h-4 shrink-0" />
              : <AlertCircle  className="w-4 h-4 shrink-0" />
            }
            {toast.msg}
          </div>
        )}

        {/* Modification warning */}
        {hasModified && (
          <div className="mx-4 mt-3 flex items-start gap-2 px-3.5 py-2.5
                          bg-amber-50 border border-amber-200 rounded-xl
                          text-xs text-amber-700 shrink-0">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              חלק מהכמויות שונו מהבקשה המקורית.
              {editable && ' שינויים נשמרים אוטומטית.'}
            </span>
          </div>
        )}

        {/* Line items */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          <div className="flex items-center justify-between text-[10px]
                          font-semibold text-gray-400 uppercase tracking-wide
                          px-3 pb-1">
            <span>מוצר</span>
            <span>כמות / מחיר</span>
          </div>

          {lines.map(line => {
            const modified = line.approved_quantity !== line.requested_quantity;
            return (
              <div key={line.id}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors
                            ${modified ? 'bg-amber-50/60 border-amber-100' : 'bg-gray-50/60 border-transparent'}`}>
                <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-gray-100">
                  {line.product_image_url ? (
                    <img src={line.product_image_url} alt={line.product_name}
                      className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
                    {line.product_name}
                  </p>
                  <p className="text-[10px] text-gray-400 font-mono" dir="ltr">{line.product_sku}</p>
                  <p className="text-xs text-gray-500">{fmtPrice(line.unit_price)} / יח׳</p>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                  <QtyCell line={line} editable={editable} onChange={handleQtyChange} saving={saving} />
                  <p className="text-sm font-bold text-gray-900 tabular-nums">
                    {fmtPrice(line.total_line_price)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-white space-y-3 shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">סה״כ הזמנה (לפני מע״מ)</span>
            <span className="text-lg font-bold text-gray-900">{fmtPrice(localTotal)}</span>
          </div>

          {canEdit && status !== 'locked' && status !== 'cancelled' && (
            <div className="flex flex-col gap-2">
              {status === 'pending' && (
                <button type="button"
                  onClick={() => transition('b2b_set_order_processing', 'processing', 'ההזמנה סומנה כ"בטיפול"')}
                  disabled={saving}
                  className="flex items-center justify-center gap-2 w-full py-2
                             rounded-xl bg-white border border-blue-200 text-blue-600
                             hover:bg-blue-50 text-sm font-medium transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                  סמן כ"בטיפול" (אופציונלי)
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowWarehousePicker(true)}
                disabled={saving}
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5
                           rounded-xl bg-teal-600 hover:bg-teal-700 text-white
                           text-sm font-semibold transition-all active:scale-[.98]
                           disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                חתום וסגור הזמנת קונסגנציה
              </button>

              <ConfirmButton
                label="בטל הזמנה"
                labelConfirm="לחץ שוב לביטול סופי ←"
                onClick={() => transition('b2b_cancel_order', 'cancelled', 'ההזמנה בוטלה')}
                disabled={saving}
                className="border border-red-200 text-red-600 hover:bg-red-50 w-full bg-white"
                icon={<XCircle className="w-4 h-4" />}
              />
            </div>
          )}

          {(status === 'locked' || status === 'cancelled') && (
            <div className={`flex items-center gap-2 text-sm font-medium px-4 py-3
                             rounded-xl border
                             ${status === 'locked'
                               ? 'bg-teal-50 border-teal-200 text-teal-700'
                               : 'bg-gray-100 border-gray-200 text-gray-500'
                             }`}>
              {status === 'locked'
                ? <><Lock className="w-4 h-4 shrink-0" /> הזמנת קונסגנציה סגורה — תעודת העברה בין מחסנים הופקה</>
                : <><XCircle className="w-4 h-4 shrink-0" /> ההזמנה בוטלה</>
              }
            </div>
          )}
        </div>
      </div>

      {showWarehousePicker && (
        <WarehousePickerModal
          companyId={companyId}
          destinationWarehouseName={destinationWarehouseName || 'מחסן הלקוח'}
          onConfirm={lockWithWarehouse}
          onCancel={() => setShowWarehousePicker(false)}
        />
      )}
    </>,
    document.body,
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ConsignmentOrdersAdminTab({
  companyId,
  onPendingCount,
}: {
  companyId:       string;
  onPendingCount?: (count: number) => void;
}) {
  const { can } = usePermissions();
  const canEdit = can({ module: 'inventory', level: 'editor' });

  const [orders,   setOrders]   = useState<AdminOrder[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminOrder | null>(null);
  const [vatRate,  setVatRate]  = useState<number>(17);
  const [checked,  setChecked]  = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!companyId) return;
    supabase
      .rpc('get_company_vat_rate', { p_company_id: companyId })
      .then(({ data }) => { if (data != null) setVatRate(Number(data)); });
  }, [companyId]);

  type Filter = 'all' | OrderStatus;
  const [filter, setFilter] = useState<Filter>('all');

  const fetchOrders = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: dbErr } = await supabase
        .from('b2b_orders')
        .select(`
          id, status, total_amount, created_at, updated_at, client_id,
          inventory_clients ( name, email, phone ),
          b2b_order_lines (
            id, product_id, requested_quantity, approved_quantity,
            unit_price, total_line_price,
            inventory_products ( name, sku, image_url )
          )
        `)
        .eq('company_id', companyId)
        .eq('order_type', 'consignment')
        .order('created_at', { ascending: false });

      if (dbErr) throw dbErr;

      const mapped: AdminOrder[] = (data ?? []).map((o: any) => {
        const client   = o.inventory_clients as { name: string; email?: string|null; phone?: string|null } | null;
        const rawLines = (o.b2b_order_lines ?? []) as any[];
        return {
          id:           o.id,
          short_id:     shortId(o.id),
          status:       o.status as OrderStatus,
          total_amount: Number(o.total_amount ?? 0),
          created_at:   o.created_at,
          updated_at:   o.updated_at,
          client_id:    o.client_id,
          client_name:  client?.name  ?? '—',
          client_email: client?.email ?? null,
          client_phone: client?.phone ?? null,
          lines: rawLines.map(l => {
            const prod = l.inventory_products as { name: string; sku: string; image_url?: string|null } | null;
            return {
              id:                 l.id,
              product_id:         l.product_id,
              product_name:       prod?.name     ?? 'מוצר לא זמין',
              product_sku:        prod?.sku       ?? '',
              product_image_url:  prod?.image_url ?? null,
              requested_quantity: l.requested_quantity,
              approved_quantity:  l.approved_quantity,
              unit_price:         Number(l.unit_price ?? 0),
              total_line_price:   Number(l.total_line_price ?? 0),
            };
          }),
        };
      });

      setOrders(mapped);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleOrderUpdated = useCallback((updated: AdminOrder) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
    setSelected(updated);
  }, []);

  const visible = useMemo(
    () => filter === 'all' ? orders : orders.filter(o => o.status === filter),
    [orders, filter],
  );

  const toggleChecked = (id: string) =>
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allVisibleChecked = visible.length > 0 && visible.every(o => checked.has(o.id));
  const toggleAllVisible = () =>
    setChecked(prev => {
      const next = new Set(prev);
      if (allVisibleChecked) visible.forEach(o => next.delete(o.id));
      else visible.forEach(o => next.add(o.id));
      return next;
    });

  const checkedPrintable = useMemo(
    () => orders.filter(o => checked.has(o.id)).map(o => toPrintable(o, vatRate)),
    [orders, checked, vatRate],
  );

  const counts = useMemo(() => {
    const c: Partial<Record<OrderStatus, number>> = {};
    for (const o of orders) c[o.status] = (c[o.status] ?? 0) + 1;
    return c;
  }, [orders]);

  const pendingCount = counts['pending'] ?? 0;

  useEffect(() => { onPendingCount?.(pendingCount); }, [pendingCount, onPendingCount]);

  return (
    <div className="space-y-4" dir="rtl">

      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Boxes className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-semibold text-gray-800">הזמנות B2B קונסגנציה</h2>
          {pendingCount > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full
                             bg-amber-100 text-amber-700 border border-amber-200">
              {pendingCount} חדשות
            </span>
          )}
        </div>
        <button onClick={fetchOrders} disabled={loading}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-500
                     hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-100
                     transition-colors disabled:opacity-40">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          רענן
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {((['all', 'pending', 'processing', 'locked', 'cancelled'] as const)).map(f => {
          const isActive = filter === f;
          const count    = f === 'all' ? orders.length : (counts[f] ?? 0);
          const meta     = f !== 'all' ? STATUS_META[f] : null;
          return (
            <button key={f} onClick={() => setFilter(f)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs
                          font-semibold border transition-colors
                          ${isActive
                            ? (meta ? meta.pill : 'bg-gray-900 text-white border-gray-900')
                            : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                          }`}>
              {meta?.icon}
              {f === 'all' ? 'הכל' : meta?.label}
              <span className={`text-[10px] font-bold px-1 py-0.5 rounded-full min-w-[18px]
                                text-center leading-none
                                ${isActive ? 'bg-white/30' : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <SelectionBar count={checkedPrintable.length} onClear={() => setChecked(new Set())}>
        <OrderPrintActions orders={checkedPrintable} title={PRINT_TITLE} filePrefix="הזמנות קונסגנציה" />
      </SelectionBar>

      {loading && (
        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">טוען הזמנות קונסגנציה…</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50
                        border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          שגיאה בטעינת הזמנות: {error}
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
          <Boxes className="w-12 h-12 text-gray-200" />
          <p className="text-sm font-medium">
            {filter === 'all' ? 'אין הזמנות קונסגנציה עדיין' : 'אין הזמנות בסטטוס זה'}
          </p>
        </div>
      )}

      {!loading && !error && visible.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center bg-teal-50/40 border-b border-gray-100">
          <div className="pr-4 pl-1 flex items-center">
            <RowCheckbox checked={allVisibleChecked} onChange={toggleAllVisible} label="בחר הכל" />
          </div>
          <div className="flex-1 grid grid-cols-[auto_1fr_1fr_auto_auto_auto]
                          items-center px-4 py-2.5 text-xs font-semibold
                          text-gray-500 uppercase tracking-wide gap-4">
            <span className="w-24">מס׳ הזמנה</span>
            <span>לקוח</span>
            <span>תאריך</span>
            <span className="w-20 text-left">סה״כ</span>
            <span className="w-28 text-center">סטטוס</span>
            <span className="w-8" />
          </div>
          </div>

          {visible.map(order => {
            const meta        = STATUS_META[order.status];
            const hasModified = order.lines.some(l => l.approved_quantity !== l.requested_quantity);
            return (
              <div key={order.id}
                className={`flex items-center border-b border-gray-50 last:border-0
                            ${checked.has(order.id) ? 'bg-teal-50/70' : meta.row}`}>
              <div className="pr-4 pl-1 flex items-center">
                <RowCheckbox checked={checked.has(order.id)} onChange={() => toggleChecked(order.id)}
                  label={`בחר הזמנה ${order.short_id}`} />
              </div>
              <button type="button" onClick={() => setSelected(order)}
                className="flex-1 grid grid-cols-[auto_1fr_1fr_auto_auto_auto]
                            items-center px-4 py-3.5 gap-4 text-right
                            hover:bg-teal-50/40 transition-colors">
                <span className="w-24 font-mono text-xs font-bold text-gray-600
                                 bg-gray-100 px-2 py-1 rounded-lg text-center">
                  #{order.short_id}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{order.client_name}</p>
                  {order.client_email && (
                    <p className="text-xs text-gray-400 truncate">{order.client_email}</p>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  <p>{fmtDate(order.created_at)}</p>
                  <p className="text-xs text-gray-400">{order.lines.length} פריטים</p>
                </div>
                <span className="w-20 text-sm font-bold text-gray-900 text-left tabular-nums">
                  {fmtPrice(order.total_amount)}
                </span>
                <div className="w-28 flex flex-col items-center gap-1">
                  <StatusBadge status={order.status} />
                  {hasModified && (
                    <span className="text-[9px] font-bold text-amber-600
                                     bg-amber-50 border border-amber-200
                                     px-1.5 py-0.5 rounded-full leading-none">
                      כמויות שונו
                    </span>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
              </button>
              </div>
            );
          })}

          <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            {visible.length} הזמנות קונסגנציה
            {filter !== 'all' && ` · ${STATUS_META[filter as OrderStatus]?.label ?? ''}`}
          </div>
        </div>
      )}

      {selected && (
        <FulfillmentDrawer
          order={selected}
          canEdit={canEdit}
          companyId={companyId}
          vatRate={vatRate}
          onClose={() => setSelected(null)}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
}
